<template>
  <div class="device-detail">
    <DeviceTeaser :device="device" />
    <div
      v-if="!device.isReachable"
      class="device-detail__not-reachable"
      v-html="str('devices.deviceIsNotReachable', { '@device': device.name })"
    ></div>
    <div class="device-detail__content">
      <TabLinks :tabs="tabs" />
      <component
        role="tabpanel"
        :aria-labelledby="`tab_${activeTab.index}`"
        :is="activeTab.component"
        :device="device"
      />
    </div>
  </div>
</template>

<script>
  import { COLOR_PRIMARY } from '@/constants.js'
  import Spinner from '@/components/icons/Spinner'
  import TabLinks from '@/components/TabLinks'
  import { ToggleButton as JsToggle } from 'vue-js-toggle-button'
  import DeviceTeaser from '@/components/devices/DeviceTeaser'

  export default {
    name: 'device-detail',
    props: {
      device: {
        type: Object,
        required: true,
      },
    },
    components: {
      DeviceTeaser,
      JsToggle,
      Spinner,
      TabLinks,
      DeviceConsumption: () => import('@/components/devices/DeviceConsumption'),
      DeviceTimer: () => import('@/components/devices/DeviceTimer'),
      DeviceSettings: () => import('@/components/devices/DeviceSettings'),
    },
    data() {
      return {
        name: '',
        description: '',
        mode: false,
        isOnChangedLocally: false,
        primaryColor: COLOR_PRIMARY,
        tabs: [
          {
            id: 'consumption',
            label: this.str('devices.consumption'),
            url: `/devices/${this.device.id}/consumption`,
            component: 'DeviceConsumption',
            disabled: false,
            default: true,
          },
          {
            id: 'timer',
            label: this.str('devices.timer'),
            url: `/devices/${this.device.id}/timer`,
            component: 'DeviceTimer',
            // Note that the device mode means from the backend's point of view.
            // Setting an automated timer is represented in the mode 'manual'.
            // See device manager backend code.
            disabled: this.device.mode === 'manual',
          },
          {
            id: 'settings',
            label: this.str('devices.settings'),
            url: `/devices/${this.device.id}/settings`,
            component: 'DeviceSettings',
            disabled: false,
          },
        ],
      }
    },
    beforeMount() {
      this.setValuesFromProp()
    },
    computed: {
      imageUrl() {
        return this.assetsPath(
          `device-types/${this.device.deviceType.toLowerCase()}.png`
        )
      },
      isOnChangedLocallyFromStore() {
        return this.$store.getters.deviceIsOnChangedLocally(this.device.id)
      },
      toggleClasses() {
        let classes = ['device-detail__toggle']
        if (this.isOnChangedLocally) {
          classes.push('device-detail__toggle--pending')
        }
        if (this.device.isOn) {
          classes.push('device-detail__toggle--active')
        }
        return classes.join(' ')
      },
      toggleLabel() {
        return this.device.isOn
          ? this.str('devices.turnOff')
          : this.str('devices.turnOn')
      },
      consumption() {
        return this.formatWattage(this.device.wattage)
      },
      temperature() {
        return this.formatTemperature(this.device.temperature)
      },
      activeTab() {
        return this.tabs
          .filter((tab) => {
            const routerTab = this.$route.params.tab
            return (!routerTab && tab.default) || tab.id === routerTab
          })
          .map((tab, index) => {
            tab.index = index
            return tab
          })[0]
      },
    },
    methods: {
      setValuesFromProp() {
        if (!this.device) {
          return
        }

        this.name = this.device.name
        this.description = this.device.description
        this.mode = this.device.mode
        this.isOnChangedLocally = this.device.isOnChangedLocally
      },
      setDeviceStatus() {
        this.$store.dispatch('toggleDeviceOn', this.device)
      },
    },
    watch: {
      isOnChangedLocallyFromStore(newValue) {
        this.isOnChangedLocally = newValue
      },
    },
  }
</script>

<style lang="scss">
  .device-detail {
    justify-content: space-between;
    padding-top: spacer(2);
    flex-wrap: wrap;
    margin-bottom: spacer(3);

    @media (min-width: $bp-xs) {
      display: flex;
    }

    @media (min-width: $bp-md) {
      margin-bottom: spacer(5);
    }

    &__name {
      flex-basis: 100%;
      font-size: $font-size-xl;
      margin-bottom: spacer(0.25);
      display: flex;
      justify-content: space-between;
      align-items: center;
      line-height: $line-height-sm;

      @media (min-width: $bp-sm) {
        font-size: $font-size-huger;
      }
    }

    &__state-unknown {
      .icon {
        width: spacer(2);
        animation: spin 1.8s linear 0s infinite;

        @media (min-width: $bp-sm) {
          width: spacer(3);
        }
      }
    }

    &__general {
      flex-basis: 100%;
      display: flex;
      justify-content: space-between;
      margin-bottom: spacer(1);
      margin-top: spacer(1);
    }

    &__description {
      @media (min-width: $bp-lg) {
        font-size: $font-size-lg;
      }
    }

    &__current {
      text-align: right;
      margin-left: spacer(2);

      &-consumption {
        font-weight: bold;
        font-size: $font-size-lg;
        transition: $transition-base;

        @media (min-width: $bp-md) {
          font-size: $font-size-xl;
        }
      }
    }

    &__img {
      margin-left: auto;
      margin-right: auto;
      max-width: 200px;

      &--not-reachable {
        opacity: 0.5;
      }

      img {
        max-width: 100%;
      }
    }

    &__not-reachable {
      flex-basis: 100%;
      text-align: center;
      margin: spacer(2) 0;
      color: $error-color;
      font-size: $font-size-lg;

      em {
        font-style: normal;
        font-weight: $font-weight-bold;
      }
    }

    &__content {
      width: 100%;
      margin-top: spacer(3);
    }

    &__mode {
      select {
        width: 100%;
      }
    }

    &__toggle {
      border: none;
      background: none;
      transition: $transition-base;
      margin-left: spacer(0.5);
      width: 3rem;

      @media (min-width: $bp-sm) {
        width: 4rem;
      }

      &:hover,
      &:active {
        color: lighten($body-color, 7%);
      }

      &--pending {
        animation: pending 1.5s ease-in-out 0s infinite;
      }

      &--active {
        color: $green;

        &:hover,
        &:active {
          color: darken($green, 5%);
        }
      }

      .icon {
        vertical-align: top;
        display: block;
      }
    }
  }
</style>
